import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchParticipants,
  addParticipant,
  updateParticipant,
  exportParticipantsThunk,
  downloadReceiptThunk,
} from "../../redux/admin/tournamentParticipants.thunk";
import { fetchTournaments } from "../../redux/admin/tournaments.thunk";
import {
  selectTournamentList,
  selectCategoriesByTournament,
} from "../../redux/admin/tournaments.selector";
import { fetchStudents } from "../../redux/admin/student.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Keyframes injected once ─── */
const injectStyles = () => {
  if (document.getElementById("tp-keyframes")) return;
  const el = document.createElement("style");
  el.id = "tp-keyframes";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=SF+Pro+Display:wght@400;500;600;700&display=swap');

    @keyframes tp-spin {
      to { transform: rotate(360deg); }
    }
    @keyframes tp-fadeIn {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes tp-slideRight {
      from { opacity: 0; transform: translateX(32px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes tp-overlayIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes tp-modalIn {
      from { opacity: 0; transform: scale(0.96) translateY(12px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes tp-shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }
    @keyframes tp-pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.4; }
    }
    .tp-row:hover { background: #f5f5f7 !important; }
    .tp-btn:hover { opacity: 0.85; }
    .tp-icon-btn:hover { background: #e8e8ed !important; }
    .tp-drawer-item:hover:not(.blocked) {
      background: #f5f5f7 !important;
      border-color: #d1d1d6 !important;
    }
    input:focus, select:focus { outline: none; border-color: #0071e3 !important; box-shadow: 0 0 0 3px rgba(0,113,227,0.15) !important; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #d1d1d6; border-radius: 3px; }
  `;
  document.head.appendChild(el);
};

/* ─── Icons ─── */
const Icon = ({ d, size = 16, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" className={className} style={style}>
    <path d={d} />
  </svg>
);
const DownloadIcon = (p) => <Icon {...p} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />;
const PlusIcon     = (p) => <Icon {...p} d="M12 5v14M5 12h14" />;
const EditIcon     = (p) => <Icon {...p} d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />;
const ReceiptIcon  = (p) => <Icon {...p} d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />;
const CloseIcon    = (p) => <Icon {...p} d="M18 6 6 18M6 6l12 12" />;
const BoltIcon     = (p) => <Icon {...p} d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />;
const SearchIcon   = (p) => <Icon {...p} d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />;
const ChevronLeft  = (p) => <Icon {...p} d="m15 18-6-6 6-6" />;
const ChevronRight = (p) => <Icon {...p} d="m9 18 6-6-6-6" />;
const UserIcon     = (p) => <Icon {...p} d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />;
const CheckIcon    = (p) => <Icon {...p} d="M20 6 9 17l-5-5" />;

/* ─── Spinner ─── */
const Spinner = ({ size = 18, color = "#ffffff", thickness = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    style={{ animation: "tp-spin 0.75s linear infinite", flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.25" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra }) => (
  <div style={{
    width: w, height: h, borderRadius: r,
    background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
    backgroundSize: "600px 100%",
    animation: "tp-shimmer 1.4s infinite linear",
    ...extra,
  }} />
);

/* ─── Helpers ─── */
const initials = (name = "") =>
  name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

const avatarColor = (name = "") => {
  const palette = [
    { bg: "#e8f0fe", fg: "#1a73e8" },
    { bg: "#fce8e6", fg: "#d93025" },
    { bg: "#e6f4ea", fg: "#1e8e3e" },
    { bg: "#fef7e0", fg: "#f9ab00" },
    { bg: "#f3e8fd", fg: "#7c3aed" },
    { bg: "#fce4ec", fg: "#c2185b" },
  ];
  const idx = name.charCodeAt(0) % palette.length || 0;
  return palette[idx];
};

const calculateFees = (player) => {
  const entry = Number(player.entry_fee || 0);
  const additional =
    player.cdca_id && player.cdca_id.trim() !== ""
      ? 0
      : Number(player.additional_fee || 0);
  return { ...player, entry_fee: entry, additional_fee: additional, total_fee: entry + additional };
};

/* ─── Design tokens ─── */
const T = {
  bg:          "#ffffff",
  bgSub:       "#f5f5f7",
  bgCard:      "#ffffff",
  border:      "#d2d2d7",
  borderLight: "#e8e8ed",
  text:        "#1d1d1f",
  textSec:     "#6e6e73",
  textMuted:   "#aeaeb2",
  blue:        "#0071e3",
  blueHover:   "#0077ed",
  blueDark:    "#004f9e",
  blueLight:   "#e8f0fe",
  blueBorder:  "#c5d8f8",
  green:       "#1c7c41",
  greenLight:  "#eaf4ee",
  greenBorder: "#a8d5b8",
  red:         "#dc2626",
  r:           10,
  rLg:         14,
  rXl:         18,
  shadow:      "0 1px 2px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.06)",
  shadowLg:    "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const TournamentParticipants = () => {
  useEffect(() => { injectStyles(); }, []);

  const dispatch = useDispatch();

  const authUser = useSelector(s => s.auth?.user || s.admin?.auth?.user || null);
  const activeUserId = authUser?.id || localStorage.getItem("userId") || "";

  const {
    participants = [],
    meta = { page: 1, total_pages: 1, total: 0 },
    loading = false,
  } = useSelector(s => s.admin?.tournamentParticipants || {});

  const { students = [], loading: studentsLoading = false } =
    useSelector(s => s.admin?.students || {});

  const tournaments = useSelector(selectTournamentList);

  /* ── UI state ── */
  const [tournamentId,    setTournamentId]    = useState("");
  const [search,          setSearch]          = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showModal,       setShowModal]       = useState(false);
  const [isEditMode,      setIsEditMode]      = useState(false);
  const [editId,          setEditId]          = useState(null);
  const [showDrawer,      setShowDrawer]      = useState(false);
  const [activePlayerIdx, setActivePlayerIdx] = useState(null);
  const [drawerSearch,    setDrawerSearch]    = useState("");

  /* ── Saving / receipt loading state ── */
  const [saving,           setSaving]          = useState(false);
  const [exportingExcel,   setExportingExcel]  = useState(false);
  const [receiptLoading,   setReceiptLoading]  = useState({}); // { [id]: bool }

  const categories = useSelector(selectCategoriesByTournament(tournamentId)) || [];
  const selectedTournament = tournaments.find(t => String(t.id) === String(tournamentId));

  const [contact, setContact] = useState({ contact_name: "", contact_phone: "", contact_email: "" });
  const [payment, setPayment] = useState({ payment_mode: "", total_amount: 0 });

  const buildInitialPlayer = (trn) => ({
    player_name: "", dob: "", category: "", gender: "Male",
    entry_fee:      Number(trn?.entry_fee || 0),
    additional_fee: Number(trn?.additional_fee || 0),
    additional_label: trn?.additional_fee_label || "Additional fee",
    total_fee: Number(trn?.entry_fee || 0) + Number(trn?.additional_fee || 0),
    cdca_id: "", tnsca_id: "", fide_id: "", student_id: null,
  });

  const [players, setPlayers] = useState([buildInitialPlayer(selectedTournament)]);

  /* ── Bootstrap ── */
  useEffect(() => {
    setTournamentId(""); setSearch(""); setDebouncedSearch("");
    setShowModal(false); setIsEditMode(false); setEditId(null);
    dispatch(fetchTournaments());
    dispatch(fetchStudents({ page: 1, limit: 100, search: "" }));
  }, [dispatch, activeUserId]);

  /* ── Debounce ── */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  /* ── Load participants ── */
  const loadData = useCallback((page = 1) => {
    if (!tournamentId) return;
    dispatch(fetchParticipants({ tournament_id: tournamentId, page, limit: 12, search: debouncedSearch }));
  }, [dispatch, tournamentId, debouncedSearch]);

  useEffect(() => { loadData(1); }, [loadData]);

  /* ── Auto-total ── */
  useEffect(() => {
    const total = players.reduce((s, p) =>
      s + Number(p.entry_fee || 0) + (p.cdca_id ? 0 : Number(p.additional_fee || 0)), 0);
    setPayment(prev => ({ ...prev, total_amount: total }));
  }, [players]);

  /* ── Handlers ── */
  const handlePageChange = (p) => {
    if (p >= 1 && p <= meta.total_pages) loadData(p);
  };

  const handleAddPlayer = () =>
    setPlayers([...players, buildInitialPlayer(selectedTournament)]);

  const handleRemovePlayer = (i) =>
    setPlayers(prev => prev.filter((_, idx) => idx !== i));

  const openAddModal = () => {
    if (!tournamentId) return toastr.warning("Please select a tournament first");
    setIsEditMode(false); setEditId(null);
    setPlayers([buildInitialPlayer(selectedTournament)]);
    setContact({ contact_name: "", contact_phone: "", contact_email: "" });
    setPayment({ payment_mode: "", total_amount: 0 });
    setShowModal(true);
  };

  const openEditModal = (p) => {
    setEditId(p.id); setIsEditMode(true);
    setContact({ contact_name: p.contact_name || "", contact_phone: p.contact_phone || "", contact_email: p.contact_email || "" });
    setPayment({ payment_mode: p.payment_mode || "", total_amount: Number(p.total_amount || 0) });
    setPlayers([{
      player_name:    p.player_name || "",
      dob:            p.dob === "0000-00-00" ? "" : p.dob,
      category:       p.category || "",
      gender:         p.gender === "M" ? "Male" : "Female",
      entry_fee:      Number(p.entry_fee || 0),
      additional_fee: Number(p.cdca_fee || p.additional_fee || 0),
      additional_label: selectedTournament?.additional_fee_label || "Additional fee",
      cdca_id:   p.cdca_id   || "",
      fide_id:   p.fide_id   || "",
      tnsca_id:  p.tnsca_id  || "",
      total_fee: Number(p.entry_fee || 0) + Number(p.additional_fee || 0),
      student_id: p.student_id || null,
    }]);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const formattedPlayers = players.map(p => ({
        ...p,
        gender:   p.gender === "Male" ? "M" : "F",
        fide_id:  p.fide_id  || null,
        tnsca_id: p.tnsca_id || null,
      }));

      if (isEditMode) {
        await dispatch(updateParticipant({
          id: editId, ...formattedPlayers[0], ...contact, ...payment,
        })).unwrap();
        setEditId(null);
        toastr.success("Record updated");
      } else {
        await dispatch(addParticipant({
          tournament_id: tournamentId, ...contact, ...payment, players: formattedPlayers,
        })).unwrap();
        toastr.success("Registration successful");
      }
      setShowModal(false);
      loadData(meta.page);
    } catch (err) {
      toastr.error(err || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!tournamentId) return toastr.warning("Select a tournament first");
    setExportingExcel(true);
    try {
      const result = await dispatch(exportParticipantsThunk({ tournament_id: tournamentId }));
      if (exportParticipantsThunk.rejected.match(result)) throw new Error(result.payload);
      const url = window.URL.createObjectURL(result.payload);
      const a = document.createElement("a");
      a.href = url; a.download = `${selectedTournament?.name || "Participants"}.xls`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toastr.success("Download started");
    } catch (error) {
      toastr.error(error.message);
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDownloadReceipt = async (participantId) => {
    setReceiptLoading(prev => ({ ...prev, [participantId]: true }));
    try {
      const result = await dispatch(downloadReceiptThunk({ participant_id: participantId }));
      if (downloadReceiptThunk.rejected.match(result)) throw new Error(result.payload);
      const url = window.URL.createObjectURL(result.payload);
      const a = document.createElement("a");
      a.href = url;
      const receiptNo = `AADH-TRN-${new Date().getFullYear()}-${String(participantId).padStart(6, "0")}`;
      a.download = `${receiptNo}.pdf`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (err) {
      toastr.error(err.message);
    } finally {
      setReceiptLoading(prev => ({ ...prev, [participantId]: false }));
    }
  };

  /* ── Drawer ── */
  const openRosterDrawer = (index) => {
    setActivePlayerIdx(index); setDrawerSearch(""); setShowDrawer(true);
  };

  const handleSelectStudent = (student) => {
    if (activePlayerIdx === null) return;
    const alreadyAdded = players.some((p, i) => i !== activePlayerIdx && p.student_id === student.id);
    if (alreadyAdded) { toastr.warning(`${student.name} is already added`); return; }
    const alreadyRegistered = participants.some(p => p.student_id === student.id);
    if (alreadyRegistered) { toastr.error(`${student.name} is already registered`); return; }

    const updated = [...players];
    updated[activePlayerIdx] = {
      ...updated[activePlayerIdx],
      player_name: student.last_name
        ? `${student.name} ${student.last_name}`.trim()
        : student.name || "",
      dob:      student.dob === "0000-00-00" ? "" : student.dob || "",
      gender:   student.gender === "F" ? "Female" : "Male",
      tnsca_id: student.tnsca_id || "",
      fide_id:  student.fide_id  || "",
      student_id: student.id,
    };
    setPlayers(updated.map(calculateFees));
    setShowDrawer(false); setActivePlayerIdx(null);
    toastr.info(`Profile loaded for ${student.name}`);
  };

  const addedStudentIds = new Set(
    players.filter((p, i) => i !== activePlayerIdx && p.student_id).map(p => p.student_id)
  );
  const registeredStudentIds = new Set(
    participants.filter(p => p.student_id).map(p => p.student_id)
  );
  const filteredStudents = students.filter(s => {
    const term = drawerSearch.toLowerCase();
    const name = `${s.name || ""} ${s.last_name || ""}`.toLowerCase();
    const adm  = (s.admission_number || "").toLowerCase();
    return name.includes(term) || adm.includes(term);
  });

  const grandTotal = players.reduce((sum, p) =>
    sum + Number(p.entry_fee || 0) + (p.cdca_id ? 0 : Number(p.additional_fee || 0)), 0);

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <>
      <div style={S.page}>
        <div style={S.card}>

          {/* ── HEADER ── */}
          <header style={S.header}>
            <div>
              <div style={S.headerEyebrow}>Tournament management</div>
              <h1 style={S.h1}>
                {selectedTournament?.name || "Participant portal"}
              </h1>
            </div>
            <div style={S.headerActions}>
              <button
                className="tp-btn"
                style={S.btnOutline}
                onClick={handleDownloadExcel}
                disabled={exportingExcel}
              >
                {exportingExcel
                  ? <><Spinner size={14} color={T.blue} thickness={2} /> Exporting…</>
                  : <><DownloadIcon size={14} /> Export Excel</>}
              </button>
              <button
                className="tp-btn"
                style={S.btnPrimary}
                onClick={openAddModal}
              >
                <PlusIcon size={14} />
                New entry
              </button>
            </div>
          </header>

          {/* ── TOOLBAR ── */}
          <div style={S.toolbar}>
            <div style={S.searchWrap}>
              <SearchIcon size={14} style={{ position: "absolute", left: 11, color: T.textMuted }} />
              <input
                style={S.searchInput}
                placeholder="Search participants…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div style={S.filterGroup}>
              <label style={S.filterLabel}>Tournament</label>
              <select
                style={S.select}
                value={tournamentId}
                onChange={e => setTournamentId(e.target.value)}
              >
                <option value="">Select event…</option>
                {tournaments.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── TABLE ── */}
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr style={S.thead}>
                  {["Participant", "Guardian / Contact", "Category", "Gender", "Actions"].map((h, i) => (
                    <th key={h} style={{ ...S.th, ...(i === 4 ? { textAlign: "right" } : {}) }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  /* ── Skeleton rows ── */
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} style={S.tr}>
                      <td style={S.td}>
                        <div style={S.userCell}>
                          <Skeleton w={34} h={34} r={8} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                            <Skeleton w="60%" h={12} />
                            <Skeleton w="40%" h={10} />
                          </div>
                        </div>
                      </td>
                      <td style={S.td}><Skeleton w="70%" h={12} /><Skeleton w="45%" h={10} style={{ marginTop: 5 }} /></td>
                      <td style={S.td}><Skeleton w={64} h={22} r={6} /></td>
                      <td style={S.td}><Skeleton w={48} h={12} /></td>
                      <td style={{ ...S.td, textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <Skeleton w={60} h={28} r={8} />
                          <Skeleton w={72} h={28} r={8} />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : !tournamentId ? (
                  <tr><td colSpan={5} style={S.emptyState}>
                    <div style={S.emptyIcon}><SearchIcon size={24} /></div>
                    <p style={S.emptyTitle}>No tournament selected</p>
                    <p style={S.emptyDesc}>Choose a tournament above to view participants</p>
                  </td></tr>
                ) : participants.length === 0 ? (
                  <tr><td colSpan={5} style={S.emptyState}>
                    <div style={S.emptyIcon}><UserIcon size={24} /></div>
                    <p style={S.emptyTitle}>No participants yet</p>
                    <p style={S.emptyDesc}>Add the first entry using the button above</p>
                  </td></tr>
                ) : (
                  participants.map(p => {
                    const { bg, fg } = avatarColor(p.player_name);
                    return (
                      <tr key={p.id} className="tp-row" style={S.tr}>
                        <td style={S.td}>
                          <div style={S.userCell}>
                            <div style={{ ...S.avatar, background: bg, color: fg }}>
                              {initials(p.player_name)}
                            </div>
                            <div>
                              <span style={S.uName}>{p.player_name}</span>
                              <span style={S.uSub}>DOB: {p.dob}</span>
                            </div>
                          </div>
                        </td>
                        <td style={S.td}>
                          <span style={S.uName}>{p.contact_name || "—"}</span>
                          <span style={S.uSub}>{p.contact_phone || "No phone on file"}</span>
                        </td>
                        <td style={S.td}>
                          <span style={{
                            ...S.pill,
                            ...(p.category?.toLowerCase().includes("open") ? S.pillGreen : S.pillBlue),
                          }}>{p.category}</span>
                        </td>
                        <td style={{ ...S.td, color: T.textSec, fontSize: 13 }}>
                          {p.gender === "M" ? "Male" : "Female"}
                        </td>
                        <td style={{ ...S.td, textAlign: "right" }}>
                          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                            <button
                              className="tp-btn"
                              style={S.btnSm}
                              onClick={() => openEditModal(p)}
                            >
                              <EditIcon size={12} /> Edit
                            </button>
                            <button
                              className="tp-btn"
                              style={{ ...S.btnSm, ...S.btnSmGreen }}
                              onClick={() => handleDownloadReceipt(p.id)}
                              disabled={!!receiptLoading[p.id]}
                            >
                              {receiptLoading[p.id]
                                ? <><Spinner size={12} color={T.green} thickness={2} /> Downloading…</>
                                : <><ReceiptIcon size={12} /> Receipt</>}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── FOOTER / PAGINATION ── */}
          <footer style={S.footer}>
            <span style={S.footerStat}>
              {loading
                ? <Skeleton w={120} h={12} />
                : `Showing ${participants.length} of ${meta.total}`}
            </span>
            <div style={S.pgGroup}>
              <button
                className="tp-btn"
                style={S.btnOutline}
                disabled={meta.page <= 1 || loading}
                onClick={() => handlePageChange(meta.page - 1)}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span style={S.pgInfo}>
                {meta.page} / {meta.total_pages || 1}
              </span>
              <button
                className="tp-btn"
                style={S.btnOutline}
                disabled={meta.page >= meta.total_pages || loading}
                onClick={() => handlePageChange(meta.page + 1)}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </footer>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          REGISTRATION MODAL
      ═══════════════════════════════════════ */}
      {showModal && (
        <div style={S.overlay}>
          <div style={S.modal}>

            {/* Modal header */}
            <div style={S.modalHeader}>
              <div>
                <span style={S.modalBadge}>
                  {isEditMode ? "Edit registration" : "New registration"}
                </span>
                <h2 style={S.modalTitle}>
                  {selectedTournament?.name || "Tournament"}
                </h2>
              </div>
              <button
                className="tp-icon-btn"
                style={S.iconBtn}
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <CloseIcon size={13} />
              </button>
            </div>

            {/* Modal body */}
            <div style={S.modalBody}>

              {/* Contact section */}
              <section>
                <SectionLabel>Contact information</SectionLabel>
                <div style={S.grid3}>
                  <Field label="Guardian name">
                    <input style={S.input} value={contact.contact_name} placeholder="Full name"
                      onChange={e => setContact({ ...contact, contact_name: e.target.value })} />
                  </Field>
                  <Field label="Phone">
                    <input style={S.input} value={contact.contact_phone} placeholder="+91 00000 00000"
                      onChange={e => setContact({ ...contact, contact_phone: e.target.value })} />
                  </Field>
                  <Field label="Email">
                    <input style={S.input} value={contact.contact_email} placeholder="email@example.com"
                      onChange={e => setContact({ ...contact, contact_email: e.target.value })} />
                  </Field>
                </div>
              </section>

              {/* Players section */}
              <section>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <SectionLabel noMargin>Player details</SectionLabel>
                  {!isEditMode && (
                    <button className="tp-btn" style={{ ...S.btnSm, ...S.btnOutline }} onClick={handleAddPlayer}>
                      <PlusIcon size={12} /> Add player
                    </button>
                  )}
                </div>

                {players.map((p, i) => (
                  <div key={i} style={S.playerCard}>
                    <div style={S.playerCardTop}>
                      <button style={S.linkBtn} onClick={() => openRosterDrawer(i)}>
                        <BoltIcon size={11} />
                        {p.student_id ? "Profile linked" : "Link student profile"}
                        {p.student_id && <CheckIcon size={11} style={{ color: T.green }} />}
                      </button>
                      {!isEditMode && players.length > 1 && (
                        <button style={S.deleteBtn} onClick={() => handleRemovePlayer(i)}>
                          Remove
                        </button>
                      )}
                    </div>

                    <div style={S.grid2}>
                      <Field label="Player name">
                        <input style={S.input} value={p.player_name}
                          onChange={e => {
                            const up = [...players]; up[i].player_name = e.target.value;
                            setPlayers(up.map(calculateFees));
                          }} />
                      </Field>
                      <Field label="Category">
                        <select style={S.input} value={p.category}
                          onChange={e => {
                            const up = [...players]; up[i].category = e.target.value;
                            setPlayers(up.map(calculateFees));
                          }}>
                          <option value="">Select…</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.category_name}>{c.category_name}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Date of birth">
                        <input type="date" style={S.input} value={p.dob}
                          onChange={e => {
                            const up = [...players]; up[i].dob = e.target.value;
                            setPlayers(up.map(calculateFees));
                          }} />
                      </Field>
                      <Field label="Gender">
                        <select style={S.input} value={p.gender}
                          onChange={e => {
                            const up = [...players]; up[i].gender = e.target.value;
                            setPlayers(up.map(calculateFees));
                          }}>
                          <option>Male</option>
                          <option>Female</option>
                        </select>
                      </Field>
                      <Field label="Entry fee (₹)">
                        <input type="number" style={S.input} value={p.entry_fee}
                          onChange={e => {
                            const up = [...players]; up[i].entry_fee = e.target.value;
                            setPlayers(up.map(calculateFees));
                          }} />
                      </Field>
                      <Field
                        label={`${p.additional_label} (₹)`}
                        hint={p.cdca_id ? "Waived — CDCA ID present" : ""}
                      >
                        <input type="number"
                          style={{ ...S.input, ...(p.cdca_id ? S.inputDisabled : {}) }}
                          value={p.cdca_id ? 0 : p.additional_fee}
                          readOnly={!!p.cdca_id}
                          onChange={e => {
                            const up = [...players]; up[i].additional_fee = e.target.value;
                            setPlayers(up.map(calculateFees));
                          }} />
                      </Field>
                      <Field label="CDCA ID" hint="Entering this waives the additional fee">
                        <input style={S.input} value={p.cdca_id} placeholder="Optional"
                          onChange={e => {
                            const up = [...players]; up[i].cdca_id = e.target.value;
                            setPlayers(up.map(calculateFees));
                          }} />
                      </Field>
                      <Field label="TNSCA ID">
                        <input style={S.input} value={p.tnsca_id || ""} placeholder="Optional"
                          onChange={e => {
                            const up = [...players]; up[i].tnsca_id = e.target.value;
                            setPlayers([...up]);
                          }} />
                      </Field>
                      <Field label="FIDE ID">
                        <input style={S.input} value={p.fide_id || ""} placeholder="Optional"
                          onChange={e => {
                            const up = [...players]; up[i].fide_id = e.target.value;
                            setPlayers([...up]);
                          }} />
                      </Field>
                    </div>
                  </div>
                ))}
              </section>
            </div>

            {/* Modal footer */}
            <div style={S.modalFooter}>
              <div style={S.totalBlock}>
                <span style={S.totalLabel}>Total amount</span>
                <span style={S.totalValue}>₹{grandTotal.toLocaleString("en-IN")}</span>
              </div>
              <div style={S.paymentBlock}>
                <label style={S.fieldLabel}>Payment mode</label>
                <select
                  style={{ ...S.input, width: 160 }}
                  value={payment.payment_mode}
                  onChange={e => setPayment({ ...payment, payment_mode: e.target.value })}
                >
                  <option value="">Select</option>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="online">Online</option>
                </select>
              </div>
              <div style={S.footerBtns}>
                <button
                  className="tp-btn"
                  style={S.btnOutline}
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="tp-btn"
                  style={{ ...S.btnPrimary, minWidth: 150 }}
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving
                    ? <><Spinner size={15} color="#fff" thickness={2} />
                        {isEditMode ? "Updating…" : "Registering…"}
                      </>
                    : isEditMode ? "Update registration" : "Save registration"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════
          ROSTER DRAWER
      ═══════════════════════════════════════ */}
      {showDrawer && (
        <div style={S.drawerOverlay} onClick={() => setShowDrawer(false)}>
          <div style={S.drawer} onClick={e => e.stopPropagation()}>
            <div style={S.drawerHeader}>
              <div>
                <h3 style={S.drawerTitle}>Student roster</h3>
                <p style={S.drawerSub}>Select a profile to auto-fill player details</p>
              </div>
              <button
                className="tp-icon-btn"
                style={S.iconBtn}
                onClick={() => setShowDrawer(false)}
                aria-label="Close drawer"
              >
                <CloseIcon size={13} />
              </button>
            </div>

            <div style={S.drawerSearch}>
              <div style={{ position: "relative" }}>
                <SearchIcon size={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: T.textMuted }} />
                <input
                  autoFocus
                  style={{ ...S.input, paddingLeft: 34 }}
                  placeholder="Search by name or admission no…"
                  value={drawerSearch}
                  onChange={e => setDrawerSearch(e.target.value)}
                />
              </div>
            </div>

            <div style={S.drawerList}>
              {studentsLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} style={{ ...S.drawerItem, cursor: "default", padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
                      <Skeleton w={30} h={30} r={8} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                        <Skeleton w="55%" h={11} />
                        <Skeleton w="35%" h={9} />
                      </div>
                    </div>
                  </div>
                ))
              ) : filteredStudents.length === 0 ? (
                <p style={S.drawerEmpty}>No matching profiles found</p>
              ) : (
                filteredStudents.map(s => {
                  const isInForm      = addedStudentIds.has(s.id);
                  const isRegistered  = registeredStudentIds.has(s.id);
                  const isBlocked     = isInForm || isRegistered;
                  const blockedLabel  = isRegistered ? "Already registered" : "Already added";
                  const { bg, fg }    = avatarColor(s.name);

                  return (
                    <div
                      key={s.id}
                      className={`tp-drawer-item${isBlocked ? " blocked" : ""}`}
                      style={{
                        ...S.drawerItem,
                        ...(isBlocked ? S.drawerItemBlocked : {}),
                      }}
                      onClick={() => !isBlocked && handleSelectStudent(s)}
                    >
                      <div style={S.studentInfo}>
                        <div style={{
                          ...S.studentAvatar,
                          background: isBlocked ? T.bgSub : bg,
                          color: isBlocked ? T.textMuted : fg,
                        }}>
                          {isBlocked
                            ? <UserIcon size={13} />
                            : <span style={{ fontSize: 11, fontWeight: 600 }}>
                                {initials(`${s.name} ${s.last_name || ""}`)}
                              </span>}
                        </div>
                        <div>
                          <span style={{ ...S.studentName, ...(isBlocked ? { color: T.textMuted } : {}) }}>
                            {s.name} {s.last_name || ""}
                          </span>
                          <span style={S.studentMeta}>
                            {s.admission_number || "—"} · Grade {s.grade || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div style={S.tagGroup}>
                        {isBlocked
                          ? <span style={S.tagBlocked}>{blockedLabel}</span>
                          : <>
                              {s.tnsca_id && <span style={S.tagBlue}>TNSCA</span>}
                              {s.fide_id  && <span style={S.tagPurple}>FIDE</span>}
                            </>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── Sub-components ─── */
const SectionLabel = ({ children, noMargin }) => (
  <p style={{
    fontSize: 11, fontWeight: 600, textTransform: "uppercase",
    letterSpacing: "0.07em", color: T.blue,
    margin: 0, marginBottom: noMargin ? 0 : 14,
  }}>{children}</p>
);

const Field = ({ label, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    <label style={S.fieldLabel}>
      {label}
      {hint && <span style={S.fieldHint}> — {hint}</span>}
    </label>
    {children}
  </div>
);

/* ─── Styles ─── */
const S = {
  page: {
    background: "#f5f5f7",
    minHeight: "100vh",
    padding: "2rem 1rem",
    fontFamily: "-apple-system, 'SF Pro Display', 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
  },
  card: {
    maxWidth: 1040,
    margin: "0 auto",
    background: T.bg,
    border: `1px solid ${T.border}`,
    borderRadius: T.rXl,
    boxShadow: T.shadow,
    overflow: "hidden",
  },

  /* Header */
  header: {
    padding: "1.375rem 1.75rem",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: "1rem", flexWrap: "wrap",
    borderBottom: `1px solid ${T.borderLight}`,
  },
  headerEyebrow: {
    fontSize: 11, fontWeight: 600, textTransform: "uppercase",
    letterSpacing: "0.07em", color: T.textMuted, marginBottom: 4,
  },
  h1: {
    fontSize: 17, fontWeight: 600, color: T.text,
    margin: 0, letterSpacing: "-0.022em",
  },
  headerActions: { display: "flex", gap: 8, flexWrap: "wrap" },

  /* Buttons */
  btnPrimary: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "8px 16px", fontSize: 13, fontWeight: 500,
    borderRadius: T.r, border: "none",
    background: T.blue, color: "#fff",
    cursor: "pointer", whiteSpace: "nowrap",
    transition: "opacity 0.15s",
    minWidth: 100, justifyContent: "center",
  },
  btnOutline: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 14px", fontSize: 13, fontWeight: 500,
    borderRadius: T.r, border: `1px solid ${T.border}`,
    background: T.bg, color: T.text,
    cursor: "pointer", whiteSpace: "nowrap",
    transition: "opacity 0.15s",
  },
  btnSm: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 11px", fontSize: 12, fontWeight: 500,
    borderRadius: 8, border: `1px solid ${T.border}`,
    background: T.bg, color: T.text,
    cursor: "pointer", whiteSpace: "nowrap",
    transition: "opacity 0.15s",
  },
  btnSmGreen: {
    background: T.greenLight, color: T.green,
    border: `1px solid ${T.greenBorder}`,
  },

  /* Toolbar */
  toolbar: {
    padding: "0.875rem 1.75rem",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, background: T.bgSub,
    borderBottom: `1px solid ${T.borderLight}`,
    flexWrap: "wrap",
  },
  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchInput: {
    border: `1px solid ${T.border}`, borderRadius: T.r,
    padding: "8px 12px 8px 34px",
    width: 240, fontSize: 13, background: T.bg, color: T.text,
    outline: "none",
  },
  filterGroup: { display: "flex", alignItems: "center", gap: 8 },
  filterLabel: {
    fontSize: 11, fontWeight: 600, textTransform: "uppercase",
    letterSpacing: "0.06em", color: T.textMuted, whiteSpace: "nowrap",
  },
  select: {
    border: `1px solid ${T.border}`, borderRadius: T.r,
    padding: "8px 12px", fontSize: 13, background: T.bg, color: T.text,
    outline: "none", minWidth: 200,
  },

  /* Table */
  table: { width: "100%", borderCollapse: "collapse", minWidth: 640 },
  thead: { background: T.bgSub },
  th: {
    padding: "10px 1.75rem", fontSize: 11, fontWeight: 600,
    color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em",
    borderBottom: `1px solid ${T.borderLight}`, textAlign: "left", whiteSpace: "nowrap",
  },
  tr: { transition: "background 0.1s" },
  td: {
    padding: "13px 1.75rem", fontSize: 13,
    borderBottom: `1px solid ${T.borderLight}`,
    verticalAlign: "middle", color: T.text,
  },
  userCell: { display: "flex", alignItems: "center", gap: 10 },
  avatar: {
    width: 34, height: 34, borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, fontWeight: 600, flexShrink: 0, letterSpacing: "-0.02em",
  },
  uName: { display: "block", fontWeight: 500, fontSize: 13, color: T.text },
  uSub:  { display: "block", fontSize: 11, color: T.textMuted, marginTop: 2 },
  pill: {
    display: "inline-block", fontSize: 11, fontWeight: 500,
    padding: "3px 9px", borderRadius: 5,
  },
  pillBlue:  { background: T.blueLight, color: T.blueDark, border: `1px solid ${T.blueBorder}` },
  pillGreen: { background: T.greenLight, color: T.green, border: `1px solid ${T.greenBorder}` },

  /* Empty state */
  emptyState: {
    textAlign: "center", padding: "4rem 1rem",
    color: T.textMuted, fontSize: 13,
  },
  emptyIcon: {
    width: 48, height: 48, background: T.bgSub,
    borderRadius: 12, display: "inline-flex",
    alignItems: "center", justifyContent: "center",
    color: T.textMuted, marginBottom: 12,
  },
  emptyTitle: { fontSize: 14, fontWeight: 500, color: T.textSec, margin: "0 0 4px" },
  emptyDesc:  { fontSize: 12, color: T.textMuted, margin: 0 },

  /* Footer / Pagination */
  footer: {
    padding: "1rem 1.75rem",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    borderTop: `1px solid ${T.borderLight}`, flexWrap: "wrap", gap: 12,
  },
  footerStat: { fontSize: 12, color: T.textMuted, fontWeight: 500 },
  pgGroup: { display: "flex", alignItems: "center", gap: 10 },
  pgInfo: {
    fontSize: 12, color: T.textSec, fontWeight: 500,
    minWidth: 60, textAlign: "center",
  },

  /* Modal */
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.38)", backdropFilter: "blur(8px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: "1rem",
    animation: "tp-overlayIn 0.2s ease",
  },
  modal: {
    background: T.bg, width: "100%", maxWidth: 780,
    borderRadius: T.rXl, border: `1px solid ${T.border}`,
    boxShadow: T.shadowLg,
    display: "flex", flexDirection: "column", maxHeight: "92vh", overflow: "hidden",
    animation: "tp-modalIn 0.22s cubic-bezier(0.34,1.12,0.64,1)",
  },
  modalHeader: {
    padding: "1.375rem 1.75rem",
    borderBottom: `1px solid ${T.borderLight}`,
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
  },
  modalBadge: {
    display: "inline-block", fontSize: 10, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.06em",
    padding: "3px 8px", borderRadius: 5,
    background: T.blueLight, color: T.blue, border: `1px solid ${T.blueBorder}`,
    marginBottom: 5,
  },
  modalTitle: {
    fontSize: 16, fontWeight: 600, color: T.text, margin: 0,
    letterSpacing: "-0.022em",
  },
  iconBtn: {
    width: 30, height: 30, borderRadius: 8,
    border: `1px solid ${T.border}`, background: T.bgSub,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    color: T.textSec, flexShrink: 0, transition: "background 0.15s",
  },
  modalBody: {
    padding: "1.625rem 1.75rem",
    overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.75rem",
  },
  grid3: {
    display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12,
  },
  grid2: {
    display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12,
  },
  fieldLabel: { fontSize: 12, fontWeight: 500, color: T.textSec, display: "block" },
  fieldHint:  { fontSize: 10, fontWeight: 400, color: T.textMuted, fontStyle: "italic" },
  input: {
    width: "100%", padding: "8px 11px", fontSize: 13,
    border: `1px solid ${T.border}`, borderRadius: T.r,
    background: T.bg, color: T.text, outline: "none",
    boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputDisabled: { background: T.bgSub, color: T.textMuted, cursor: "not-allowed" },
  playerCard: {
    background: T.bgSub, border: `1px solid ${T.borderLight}`,
    borderRadius: T.r, padding: "1.125rem", marginBottom: 10,
    animation: "tp-fadeIn 0.18s ease",
  },
  playerCardTop: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 14, paddingBottom: 12,
    borderBottom: `1px dashed ${T.border}`,
  },
  linkBtn: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 12px", fontSize: 11, fontWeight: 600,
    background: T.blueLight, color: T.blue,
    border: `1px solid ${T.blueBorder}`, borderRadius: 8, cursor: "pointer",
  },
  deleteBtn: {
    background: "none", border: "none",
    color: T.red, fontSize: 11, fontWeight: 500,
    cursor: "pointer", padding: "4px 6px",
  },
  modalFooter: {
    padding: "1rem 1.75rem",
    borderTop: `1px solid ${T.borderLight}`,
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  totalBlock: { flex: 1, display: "flex", flexDirection: "column", gap: 2 },
  totalLabel: {
    fontSize: 10, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.07em", color: T.textMuted,
  },
  totalValue: {
    fontSize: 24, fontWeight: 700, color: T.text,
    letterSpacing: "-0.03em", fontVariantNumeric: "tabular-nums",
  },
  paymentBlock: { display: "flex", flexDirection: "column", gap: 4 },
  footerBtns: { display: "flex", gap: 8 },

  /* Drawer */
  drawerOverlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.22)", backdropFilter: "blur(4px)",
    zIndex: 2000, display: "flex", justifyContent: "flex-end",
    animation: "tp-overlayIn 0.2s ease",
  },
  drawer: {
    width: "100%", maxWidth: 430,
    background: T.bg, height: "100%",
    display: "flex", flexDirection: "column",
    borderLeft: `1px solid ${T.border}`,
    boxShadow: "-12px 0 40px rgba(0,0,0,0.1)",
    animation: "tp-slideRight 0.24s cubic-bezier(0.34,1.08,0.64,1)",
  },
  drawerHeader: {
    padding: "1.375rem 1.5rem",
    borderBottom: `1px solid ${T.borderLight}`,
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
  },
  drawerTitle: { fontSize: 14, fontWeight: 600, color: T.text, margin: 0 },
  drawerSub:   { fontSize: 11, color: T.textMuted, margin: "3px 0 0" },
  drawerSearch: {
    padding: "0.875rem 1.5rem",
    background: T.bgSub, borderBottom: `1px solid ${T.borderLight}`,
  },
  drawerList: {
    flex: 1, overflowY: "auto",
    padding: "0.875rem 1.5rem",
    display: "flex", flexDirection: "column", gap: 5,
  },
  drawerItem: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 12px",
    border: `1px solid ${T.borderLight}`, borderRadius: T.r,
    cursor: "pointer", transition: "all 0.12s",
    animation: "tp-fadeIn 0.15s ease",
  },
  drawerItemBlocked: {
    background: T.bgSub, opacity: 0.65, cursor: "not-allowed",
  },
  drawerEmpty: {
    textAlign: "center", padding: "3rem 1rem",
    fontSize: 12, color: T.textMuted, fontStyle: "italic",
  },
  studentInfo: { display: "flex", alignItems: "center", gap: 10 },
  studentAvatar: {
    width: 30, height: 30, borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  studentName: { display: "block", fontSize: 13, fontWeight: 500, color: T.text },
  studentMeta: { display: "block", fontSize: 10, color: T.textMuted, marginTop: 2 },
  tagGroup: { display: "flex", gap: 4 },
  tagBlue: {
    fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
    background: T.blueLight, color: T.blue, border: `1px solid ${T.blueBorder}`,
    textTransform: "uppercase", letterSpacing: "0.05em",
  },
  tagPurple: {
    fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
    background: "#f3e8fd", color: "#7c3aed", border: "1px solid #e9d5ff",
    textTransform: "uppercase", letterSpacing: "0.05em",
  },
  tagBlocked: {
    fontSize: 9, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
    background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047",
    textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap",
  },
};

export default TournamentParticipants;
